import React, { useEffect, useState } from 'react';
import { typography } from '../../styles/typography';
import { Card, PrimaryButton } from '../ui';
import { employeeApi } from './employeeApi';
import ZoneEmployeeAssignment from './ZoneEmployeeAssignment';

const ZoneManagement = ({ 
  siteId, 
  onZoneSelect, 
  selectedZoneId, 
  onAddEmployeeToZone,
  onEmployeeAdd,
  onEmployeeRemove,
  onNoteEdit,
  zoneGuards = {},
  employeeType = 'guards',
  shiftValidationData = null
}) => {
  const [zones, setZones] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAddZoneForm, setShowAddZoneForm] = useState(false);
  const [newZoneName, setNewZoneName] = useState('');
  const [expandedZones, setExpandedZones] = useState({});

  // Fetch zones when siteId changes
  useEffect(() => {
    if (siteId) {
      fetchZones();
    }
  }, [siteId]);

  const fetchZones = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const zonesData = await employeeApi.getZonesBySiteId(siteId);
      
      // Process zones data to match expected structure
      const processedZones = zonesData.map(zone => ({
        zone_id: zone.b_id || zone.zone_id,
        zone_name: zone.zone?.name || zone.zone_name || 'Unnamed Zone',
        description: zone.zone?.description || zone.description || '',
        site_id: siteId
      }));
      
      setZones(processedZones);
    } catch (error) {
      console.error('Error fetching zones:', error);
      setError(error.message || 'Failed to fetch zones');
      setZones([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateZone = async () => {
    if (!newZoneName.trim()) return;

    try {
      const zoneData = {
        site_id: siteId,
        zone_name: newZoneName.trim(),
        description: ''
      };

      await employeeApi.createZone(zoneData);
      
      // Refresh zones list
      await fetchZones();
      
      // Reset form
      setNewZoneName('');
      setShowAddZoneForm(false);
    } catch (error) {
      console.error('Error creating zone:', error);
      setError(error.message || 'Failed to create zone');
    }
  };

  const handleZoneClick = (zoneId) => {
    onZoneSelect(zoneId);
  };

  const handleAddEmployeeToZone = (zoneId) => {
    onAddEmployeeToZone(zoneId);
  };

  const handleEmployeeAdd = (zoneId, employee) => {
    if (onEmployeeAdd) {
      onEmployeeAdd(zoneId, employee);
    }
  };

  const handleEmployeeRemove = (zoneId, employee) => {
    if (onEmployeeRemove) {
      onEmployeeRemove(zoneId, employee);
    }
  };

  const handleNoteEdit = (zoneId, employee, newNote) => {
    if (onNoteEdit) {
      onNoteEdit(zoneId, employee, newNote);
    }
  };

  const handleToggleZoneExpanded = (zoneId) => {
    setExpandedZones(prev => ({
      ...prev,
      [zoneId]: !prev[zoneId]
    }));
  };

  // Get all employees assigned to other zones (excluding current zone)
  const getExcludedEmployees = (currentZoneId) => {
    console.log('🔍 getExcludedEmployees called for zone:', currentZoneId, 'zoneGuards:', zoneGuards, 'zones:', zones);
    
    const excludedEmployees = [];
    Object.keys(zoneGuards).forEach(zoneId => {
      if (zoneId !== currentZoneId && zoneGuards[zoneId]) {
        const zoneName = zones.find(z => z.zone_id === zoneId)?.zone_name || 'Unknown Zone';
        console.log(`🔍 Adding employees from zone ${zoneId} (${zoneName}):`, zoneGuards[zoneId]);
        
        excludedEmployees.push(...zoneGuards[zoneId].map(emp => {
          const processedEmp = {
            ...emp,
            assignedToZoneId: zoneId,
            assignedToZoneName: zoneName
          };
          console.log('🔍 Processed excluded employee:', processedEmp);
          return processedEmp;
        }));
      }
    });
    
    console.log('🔍 Final excluded employees:', excludedEmployees);
    return excludedEmployees;
  };

  if (isLoading) {
    return (
      <div className="w-full">
        <div className="text-center text-gray-500 py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-2"></div>
          Loading zones...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full">
        <div className="text-center text-red-500 py-8">
          <div className="mb-2">⚠️</div>
          <div className="text-sm mb-4">{error}</div>
          <button 
            onClick={fetchZones}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full space-y-4">
      {/* Header with Add Zone Button */}
      <div className="flex justify-between items-center">
        <h3 className={typography.h3} style={{ color: '#000000' }}>
          Zones for this Site
        </h3>
        {/* <PrimaryButton
          onClick={() => setShowAddZoneForm(!showAddZoneForm)}
          className="text-sm px-4 py-2"
        >
          + Add Zone
        </PrimaryButton> */}
      </div>

      {/* Add Zone Form */}
      {showAddZoneForm && (
        <Card.Gradient className="p-4">
          <div className="space-y-3">
            <h4 className={typography.h5} style={{ color: '#000000' }}>
              Create New Zone
            </h4>
            <div className="flex gap-2">
              <input
                type="text"
                value={newZoneName}
                onChange={(e) => setNewZoneName(e.target.value)}
                placeholder="Enter zone name..."
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                onKeyPress={(e) => e.key === 'Enter' && handleCreateZone()}
              />
              <PrimaryButton
                onClick={handleCreateZone}
                disabled={!newZoneName.trim()}
                className="px-4 py-2"
              >
                Create
              </PrimaryButton>
              <button
                onClick={() => {
                  setShowAddZoneForm(false);
                  setNewZoneName('');
                }}
                className="px-4 py-2 text-gray-500 hover:text-gray-700"
              >
                Cancel
              </button>
            </div>
          </div>
        </Card.Gradient>
      )}

      {/* Zones List */}
      {zones.length > 0 ? (
        <div className="space-y-3">
          {zones.map((zone) => {
            const guardsInZone = zoneGuards[zone.zone_id] || [];
            const isExpanded = expandedZones[zone.zone_id] || false;
            
            return (
              <ZoneEmployeeAssignment
                key={zone.zone_id}
                zoneId={zone.zone_id}
                zoneName={zone.zone_name}
                assignedEmployees={guardsInZone}
                excludedEmployees={getExcludedEmployees(zone.zone_id)}
                employeeType={employeeType}
                siteId={siteId}
                onEmployeeAdd={handleEmployeeAdd}
                onEmployeeRemove={handleEmployeeRemove}
                onNoteEdit={handleNoteEdit}
                isExpanded={isExpanded}
                onToggleExpanded={() => handleToggleZoneExpanded(zone.zone_id)}
                shiftValidationData={shiftValidationData}
              />
            );
          })}
        </div>
      ) : (
        <Card.Gradient className="p-8 text-center">
          <div className="text-gray-500">
            <div className="mb-4">
              <svg className="w-12 h-12 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <h4 className={typography.h5} style={{ color: '#000000', marginBottom: '0.5rem' }}>
              No Zones Available
            </h4>
            <p className="text-sm text-gray-600 mb-4">
              This site doesn't have any zones yet. Create a zone to start assigning {employeeType}.
            </p>
             {/* <PrimaryButton
               onClick={() => setShowAddZoneForm(true)}
               className="px-4 py-2"
             >
               Create First Zone
             </PrimaryButton> */}
          </div>
        </Card.Gradient>
      )}
    </div>
  );
};

export default ZoneManagement;
